import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
  Tab,
  Tabs,
  Modal,
  Form,
} from "react-bootstrap";
import { gymMembersAPI } from "../../api/gymMembers";
import { membershipAPI } from "../../api/membershipPlans";
import Swal from "sweetalert2";
import {
  FaArrowLeft,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaCalendar,
  FaMoneyBillWave,
  FaFileMedical,
  FaEdit,
  FaTrash,
  FaPrint,
  FaPlus,
  FaHistory,
  FaIdCard,
  FaClock,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";

const MemberDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [member, setMember] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddPlanModal, setShowAddPlanModal] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    mobile_number: "",
    emergency_number: "",
    email: "",
    address: "",
    medical_history: "",
    dob: null,
  });

  useEffect(() => {
    if (id) {
      fetchMemberDetails();
    } else {
      navigate("/members");
    }
  }, [id, navigate]);

  const fetchMemberDetails = async () => {
    try {
      const [memberResponse, plansResponse] = await Promise.all([
        gymMembersAPI.getMemberById(id),
        membershipAPI.getPlans(),
      ]);

      const memberData = memberResponse.data || memberResponse;
      setMember(memberData);

      // Filter plans for this member
      const allPlans = plansResponse.data?.data || plansResponse.data || [];
      const memberPlans = allPlans.filter(
        (plan) => plan.member === parseInt(id)
      );
      setPlans(memberPlans);

      // Initialize edit form
      setEditForm({
        first_name: memberData.first_name || "",
        last_name: memberData.last_name || "",
        mobile_number: memberData.mobile_number || "",
        emergency_number: memberData.emergency_number || "",
        email: memberData.email || "",
        address: memberData.address || "",
        medical_history: memberData.medical_history || "",
        dob: memberData.dob ? new Date(memberData.dob) : null,
      });
    } catch (error) {
      console.error("Error fetching member details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load member details",
      });
      navigate("/members");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      const formattedData = {
        ...editForm,
        dob: editForm.dob ? editForm.dob.toISOString().split("T")[0] : null,
      };

      await gymMembersAPI.updateMember(id, formattedData);

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Member details updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      setShowEditModal(false);
      fetchMemberDetails(); // Refresh data
    } catch (error) {
      console.error("Error updating member:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update member",
      });
    }
  };

  const handleDeleteMember = async () => {
    try {
      await gymMembersAPI.deleteMember(id);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Member has been deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/members");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete member",
      });
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy hh:mm a");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateAge = (dob) => {
    if (!dob) return "N/A";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Inactive</Badge>
    );
  };

  const getPlanStatusBadge = (plan) => {
    if (!plan.is_active) {
      return <Badge bg="secondary">Inactive</Badge>;
    }

    const expiryDate = new Date(plan.expiry_date);
    const today = new Date();
    const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return <Badge bg="danger">Expired</Badge>;
    } else if (diffDays <= 7) {
      return <Badge bg="warning">Expiring Soon</Badge>;
    } else {
      return <Badge bg="success">Active</Badge>;
    }
  };

  const getPaymentStatusBadge = (plan) => {
    const pendingAmount = parseFloat(plan.pending_amount || 0);
    if (pendingAmount <= 0) {
      return <Badge bg="success">Paid</Badge>;
    } else if (pendingAmount < parseFloat(plan.amount || 0)) {
      return <Badge bg="info">Partial</Badge>;
    } else {
      return <Badge bg="warning">Pending</Badge>;
    }
  };

  const calculateMemberStats = () => {
    const totalPlans = plans.length;
    const activePlans = plans.filter((plan) => plan.is_active).length;
    const totalPaid = plans.reduce(
      (sum, plan) => sum + parseFloat(plan.paid_amount || 0),
      0
    );
    const totalPending = plans.reduce(
      (sum, plan) => sum + parseFloat(plan.pending_amount || 0),
      0
    );
    const totalAmount = plans.reduce(
      (sum, plan) => sum + parseFloat(plan.total_amount || plan.amount || 0),
      0
    );

    return {
      totalPlans,
      activePlans,
      totalPaid,
      totalPending,
      totalAmount,
    };
  };

  const printMemberDetails = () => {
    const printContent = document.getElementById(
      "member-details-content"
    ).innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading member details...</p>
      </div>
    );
  }

  if (!member) {
    return (
      <Alert variant="danger">
        Member not found.{" "}
        <Button variant="link" onClick={() => navigate("/members")}>
          Go back to members
        </Button>
      </Alert>
    );
  }

  const stats = calculateMemberStats();

  return (
    <div className="member-details-page">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/members")}
              className="me-3"
            >
              <FaArrowLeft /> Back to Members
            </Button>
            <h2 className="mb-0 d-inline">Member Details</h2>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={printMemberDetails}>
              <FaPrint className="me-2" /> Print
            </Button>
            <Button variant="warning" onClick={() => setShowEditModal(true)}>
              <FaEdit className="me-2" /> Edit Member
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              <FaTrash className="me-2" /> Delete
            </Button>
          </div>
        </div>
        <p className="text-muted mb-0">Member ID: {member.member_id}</p>
      </div>

      <div id="member-details-content">
        {/* Quick Stats */}
        <Row className="mb-4">
          <Col md={2} sm={6}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Total Plans</h6>
                <h3>{stats.totalPlans}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2} sm={6}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Active Plans</h6>
                <h3 className="text-success">{stats.activePlans}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Total Paid</h6>
                <h3 className="text-primary">
                  {formatCurrency(stats.totalPaid)}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Pending</h6>
                <h3 className="text-warning">
                  {formatCurrency(stats.totalPending)}
                </h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2} sm={6}>
            <Card className="text-center">
              <Card.Body>
                <h6 className="text-muted">Status</h6>
                <h3>{getStatusBadge(member.is_active)}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
        >
          <Tab eventKey="overview" title="Overview">
            <Row>
              <Col lg={8}>
                {/* Personal Information Card */}
                <Card className="mb-4">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                      <FaUser className="me-2" /> Personal Information
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <h6>Basic Details</h6>
                          <div className="p-3 bg-light rounded">
                            <div className="mb-2">
                              <small className="text-muted">Full Name:</small>
                              <div className="fw-bold">
                                {member.first_name} {member.last_name}
                              </div>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">
                                Date of Birth:
                              </small>
                              <div>
                                <FaCalendar className="me-1 text-primary" />
                                {formatDate(member.dob)}
                                <span className="ms-2">
                                  ({calculateAge(member.dob)} years)
                                </span>
                              </div>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Age:</small>
                              <div className="fw-bold">
                                {calculateAge(member.dob)} years
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <h6>Contact Information</h6>
                          <div className="p-3 bg-light rounded">
                            <div className="mb-2 d-flex align-items-center">
                              <FaPhone className="me-2 text-primary" />
                              <div>
                                <div className="fw-bold">
                                  {member.mobile_number}
                                </div>
                                <small className="text-muted">Mobile</small>
                              </div>
                            </div>
                            <div className="mb-2 d-flex align-items-center">
                              <FaPhone className="me-2 text-primary" />
                              <div>
                                <div className="fw-bold">
                                  {member.emergency_number || "N/A"}
                                </div>
                                <small className="text-muted">
                                  Emergency Contact
                                </small>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <FaEnvelope className="me-2 text-primary" />
                              <div>
                                <div className="fw-bold">{member.email}</div>
                                <small className="text-muted">Email</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <h6>Address</h6>
                          <div className="p-3 bg-light rounded h-100">
                            <div className="d-flex align-items-start mb-2">
                              <FaHome className="me-2 text-primary mt-1" />
                              <div>
                                <div className="fw-bold">Current Address</div>
                                <p className="mb-0">
                                  {member.address || "No address provided"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mb-3">
                          <h6>Member Information</h6>
                          <div className="p-3 bg-light rounded">
                            <div className="mb-2">
                              <small className="text-muted">Member ID:</small>
                              <div className="fw-bold">
                                <FaIdCard className="me-1" />
                                {member.member_id}
                              </div>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">Join Date:</small>
                              <div>
                                <FaCalendar className="me-1" />
                                {formatDateTime(member.created_at)}
                              </div>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted">
                                Last Updated:
                              </small>
                              <div>
                                <FaClock className="me-1" />
                                {member.updated_at
                                  ? formatDateTime(member.updated_at)
                                  : "Never"}
                              </div>
                            </div>
                            <div>
                              <small className="text-muted">Status:</small>
                              <div>{getStatusBadge(member.is_active)}</div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Medical Information Card */}
                <Card className="mb-4">
                  <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                      <FaFileMedical className="me-2" /> Medical Information
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="p-3 bg-light rounded">
                      {member.medical_history ? (
                        <p className="mb-0">{member.medical_history}</p>
                      ) : (
                        <div className="text-center text-muted py-3">
                          <FaFileMedical size={32} className="mb-2" />
                          <p>No medical history recorded</p>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                {/* Membership Plans Summary */}
                <Card className="mb-4">
                  <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <FaMoneyBillWave className="me-2" /> Membership Plans
                    </h5>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={() => navigate("/membership/plans/add")}
                    >
                      <FaPlus className="me-1" /> Add Plan
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {plans.length === 0 ? (
                      <div className="text-center py-4">
                        <FaMoneyBillWave
                          size={48}
                          className="text-muted mb-3"
                        />
                        <h5>No membership plans</h5>
                        <p className="text-muted">
                          This member doesn't have any active plans
                        </p>
                        <Button
                          variant="primary"
                          onClick={() => navigate("/membership/plans/add")}
                        >
                          <FaPlus className="me-2" /> Create First Plan
                        </Button>
                      </div>
                    ) : (
                      <div className="plan-list">
                        {plans.slice(0, 3).map((plan) => (
                          <div
                            key={plan.id}
                            className="mb-3 p-3 border rounded"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="fw-bold">Plan #{plan.id}</div>
                              {getPlanStatusBadge(plan)}
                            </div>
                            <div className="mb-1">
                              <small className="text-muted">Amount:</small>
                              <div className="fw-bold">
                                {formatCurrency(
                                  plan.total_amount || plan.amount
                                )}
                              </div>
                            </div>
                            <div className="mb-1">
                              <small className="text-muted">Status:</small>
                              <div>{getPaymentStatusBadge(plan)}</div>
                            </div>
                            <div className="mb-1">
                              <small className="text-muted">Valid Till:</small>
                              <div>{formatDate(plan.expiry_date)}</div>
                            </div>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                navigate(`/membership/plans/${plan.id}`)
                              }
                              className="w-100 mt-2"
                            >
                              View Details
                            </Button>
                          </div>
                        ))}

                        {plans.length > 3 && (
                          <div className="text-center mt-3">
                            <Button
                              variant="outline-secondary"
                              onClick={() => setActiveTab("plans")}
                            >
                              View All {plans.length} Plans
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">Quick Actions</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-grid gap-2">
                      <Button
                        variant="success"
                        onClick={() => navigate("/membership/plans/add")}
                      >
                        <FaPlus className="me-2" />
                        Create New Plan
                      </Button>
                      <Button
                        variant="warning"
                        onClick={() => setShowEditModal(true)}
                      >
                        <FaEdit className="me-2" />
                        Edit Member
                      </Button>
                      <Button
                        variant="outline-primary"
                        onClick={() => setActiveTab("history")}
                      >
                        <FaHistory className="me-2" />
                        View Activity History
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={printMemberDetails}
                      >
                        <FaPrint className="me-2" />
                        Print Details
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Tab>

          <Tab eventKey="plans" title="Membership Plans">
            <Card>
              <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaMoneyBillWave className="me-2" /> All Membership Plans
                </h5>
                <Button
                  variant="light"
                  onClick={() => navigate("/membership/plans/add")}
                >
                  <FaPlus className="me-1" /> Add New Plan
                </Button>
              </Card.Header>
              <Card.Body>
                {plans.length === 0 ? (
                  <div className="text-center py-5">
                    <FaMoneyBillWave size={64} className="text-muted mb-3" />
                    <h4>No membership plans found</h4>
                    <p className="text-muted mb-4">
                      This member doesn't have any membership plans yet
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => navigate("/membership/plans/add")}
                    >
                      <FaPlus className="me-2" /> Create First Plan
                    </Button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover striped>
                      <thead>
                        <tr>
                          <th>Plan ID</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Payment Status</th>
                          <th>Plan Status</th>
                          <th>Start Date</th>
                          <th>Expiry Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plans.map((plan) => (
                          <tr key={plan.id}>
                            <td>
                              <div className="fw-bold">#{plan.id}</div>
                            </td>
                            <td>
                              <div>Type {plan.subscription_type}</div>
                              <small className="text-muted">
                                Period: {plan.subscription_period}
                              </small>
                            </td>
                            <td>
                              <div className="fw-bold">
                                {formatCurrency(
                                  plan.total_amount || plan.amount
                                )}
                              </div>
                              <small className="text-success">
                                Paid: {formatCurrency(plan.paid_amount)}
                              </small>
                              <br />
                              <small className="text-danger">
                                Pending: {formatCurrency(plan.pending_amount)}
                              </small>
                            </td>
                            <td>{getPaymentStatusBadge(plan)}</td>
                            <td>{getPlanStatusBadge(plan)}</td>
                            <td>{formatDate(plan.plan_start_date)}</td>
                            <td>
                              <div>{formatDate(plan.expiry_date)}</div>
                              <small className="text-muted">
                                {(() => {
                                  const expiry = new Date(plan.expiry_date);
                                  const today = new Date();
                                  const diffDays = Math.ceil(
                                    (expiry - today) / (1000 * 60 * 60 * 24)
                                  );
                                  return `${diffDays} days left`;
                                })()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/membership/plans/${plan.id}`)
                                  }
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() =>
                                    navigate(
                                      `/membership/plans/edit/${plan.id}`
                                    )
                                  }
                                >
                                  Edit
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="history" title="Activity History">
            <Card>
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">
                  <FaHistory className="me-2" /> Member Activity History
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="text-center py-5">
                  <FaHistory size={64} className="text-muted mb-3" />
                  <h4>Activity History</h4>
                  <p className="text-muted">
                    Member's activity history will be displayed here
                  </p>
                  <div className="mt-4">
                    <Table bordered>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Activity</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{formatDate(member.created_at)}</td>
                          <td>Member Registration</td>
                          <td>Member was registered in the system</td>
                        </tr>
                        {plans.map((plan) => (
                          <tr key={plan.id}>
                            <td>{formatDate(plan.created_at)}</td>
                            <td>Plan Created</td>
                            <td>Membership plan #{plan.id} was created</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </div>

      {/* Edit Member Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Member Details</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, first_name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, last_name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <DatePicker
                selected={editForm.dob}
                onChange={(date) => setEditForm({ ...editForm, dob: date })}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                maxDate={new Date()}
                showYearDropdown
                dropdownMode="select"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.mobile_number}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        mobile_number: e.target.value,
                      })
                    }
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Emergency Contact Number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.emergency_number}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        emergency_number: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={editForm.address}
                onChange={(e) =>
                  setEditForm({ ...editForm, address: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Medical History</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editForm.medical_history}
                onChange={(e) =>
                  setEditForm({ ...editForm, medical_history: e.target.value })
                }
                placeholder="Any medical conditions, allergies, or special requirements..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <FaTrash className="me-2" />
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>
            Are you sure you want to delete member{" "}
            <strong>
              {member.first_name} {member.last_name}
            </strong>
            ?
          </p>
          <p className="text-muted">
            This will permanently delete the member and all associated plans and
            payment records.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteMember}>
            Delete Member
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MemberDetails;
